// src/pages/CodonOccupancy/index.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Container, Row, Col, Button, ButtonGroup } from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import { FadeLoader } from "react-spinners";

// Common Components
import BreadCrumb from "../../Components/Common/BreadCrumb";
import GeneralSettings from "../../Components/Common/GeneralSettings";
import Files from "../../Components/Common/Files";
import SelectedFilesDisplay from "../../Components/Common/SelectedFilesDisplay";
import PlotContainer from "../../Components/Common/PlotContainer";
import SeqTypesAndGroups from "../../Components/Common/SeqTypesAndGroups";

// Page-specific Components
import SingleGroupManager from "./components/SingleGroupManager";
import GroupManager from "./components/GroupManager";

// API
import {
  fetchCodonOccupancyData,
  submitCodonOccupancyJob,
} from "../../helpers/eirna_backend_helper";

// Hooks
import { useSettings } from "../../hooks/useSettings";
import { useFileSelection } from "../../hooks/useFileSelection";
import { useSmartReadLength } from "../../hooks/useSmartReadLength";
import { useJobPolling } from "../../hooks/useJobPolling";
import { useGroupsManager } from "../../hooks/useGroupsManager";
import { useSingleGroup } from "./hooks/useSingleGroup";
import { EnterButtonListener } from "../../hooks/EnterButtonListener";
import { useScrollToRef } from "../../hooks/useScrollToRef";

// Short code handling
import {
  useCodonOccupancyShortCode,
  createPlotSpecificPayload,
} from "../../hooks/useShortCodeHandlers";

// Config
import {
  codonOccupancyConfig,
  codonOccupancyDefaults,
  codonOccupancyValidation,
} from "../../config/plotConfigs/codonOccupancyConfig";
import ActionButton from "../../Components/Common/ActionButton";
import InfoTooltip from "../../Components/Common/InfoTooltip";

const CodonOccupancy = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // State
  const [loading, setLoading] = useState(true);
  const [plotData, setPlotData] = useState(null);
  const [error, setError] = useState(null);
  const [chooserOpen, setChooserOpen] = useState(false);

  // References to plotContainer and loadingDiv, to allow for auto scrolling
  const loadingDivRef  = useRef(null);
  const plotContainerRef  = useRef(null);

  // Analysis mode state (separate from settings)
  const [analysisMode, setAnalysisMode] = useState("single");

  const [apiData, setApiData] = useState({
    json_files: [],
    organism: "",
    transcriptome: "",
  });

  // Hooks
  const {
    settings,
    updateSetting,
    resetSettings,
    validateSettings,
    updateMultipleSettings,
  } = useSettings(codonOccupancyDefaults, codonOccupancyValidation);

  const {
    selectedFiles,
    selectedSeqType,
    selectedGroup,
    toggleFile,
    toggleAllFiles,
    setSelectedSeqType,
    setSelectedGroup,
    clearSelection,
    selectMultipleFiles,
    deselectMultipleFiles,
    setSelectedFiles,
  } = useFileSelection("riboseq");

  const {
    getStandardReadLengths,
    markFieldAsUserModified,
    resetUserModifications,
    shouldAutoAdjust,
  } = useSmartReadLength();

  const { plotLoading, taskId, startJob } = useJobPolling();

  // Multi groups manager hook (for comparison analysis mode)
  const {
    groups,
    addFilesToGroup,
    removeFilesFromGroup,
    updateGroupName,
    clearGroup,
    clearAllGroups,
    handleAutoFill,
    handleAutoLabel,
    resetGroups,
  } = useGroupsManager([
    { id: "group1", name: "Group 1", files: [] },
    { id: "group2", name: "Group 2", files: [] },
  ]);

  // Use the Codon Occupancy specific short code handler
  const { autoRunPlot, 
          setAutoRunPlot,
          consumeUserPassedShortCode } =
    useCodonOccupancyShortCode({
      location,
      updateMultipleSettings,
      setSelectedFiles,
      resetGroups,
      setAnalysisMode,
    });

  // A callback used by the EnterButtonListener to generate the plot
  const runPlot = useCallback(() => {
    if (plotLoading) return;
    setAutoRunPlot(true);
  }, [plotLoading]);

  // Listens for the Enter button being pressed, if so calls runPlot (as long as
  // a textarea is not in focus)
  EnterButtonListener({
    onEnter: runPlot,
    modalOpen: chooserOpen,
    enabled: true,
  });

  // Scroll to loadingDiv anytime plotLoading changes
  useScrollToRef(loadingDivRef, plotLoading);

  // Scroll to plot/table anytime plotData changes
  useScrollToRef(plotContainerRef, plotData);


  // Load data on component mount
  useEffect(() => {
    if (!projectId) return;

    const loadData = async () => {
      try {
        setLoading(true);
        const response = await fetchCodonOccupancyData(projectId);
        const data = response?.data || response || {};

        setApiData({
          json_files: data.json_files || [],
          organism: data.organism || "",
          transcriptome: data.transcriptome || "",
        });

        // Set default seq type
        const fileTypes = data.json_files
          ? [...new Set(data.json_files.map((x) => x.file_type))]
          : [];
        const defaultSeqType = fileTypes.includes("riboseq")
          ? "riboseq"
          : fileTypes[0];
        if (defaultSeqType) {
          setSelectedSeqType(defaultSeqType);
        }

        // Set default group
        const uniqueGroups = [
          ...new Set(
            data.json_files?.map((file) => file.subgroup).filter(Boolean) || []
          ),
        ];
        if (uniqueGroups.length > 0) {
          setSelectedGroup(uniqueGroups[0]);
        }

        toast.success("Project data loaded successfully");
      } catch (err) {
        console.error("Failed to load project data:", err);
        const errorMsg = "Failed to load project data";
        setError(errorMsg);
        toast.error(errorMsg);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [projectId, setSelectedSeqType, setSelectedGroup]);

  // Auto-trigger analysis generation when autoRunPlot is true
  useEffect(() => {
    if (autoRunPlot) {
      console.log("Auto-run triggered, checking conditions...", {
        autoRunPlot,
        plotLoading,
        loading,
        selectedFilesCount: selectedFiles.length,
        groupsWithFiles: groups.filter((g) => g.files.length > 0).length,
        analysisMode,
      });

      const hasFilesForSingle =
        analysisMode === "single" && selectedFiles.length > 0;
      const hasFilesForComparison =
        analysisMode === "comparison" &&
        groups.length >= 2 &&
        groups.every((g) => g.files.length > 0);

      if (
        !plotLoading &&
        !loading &&
        (hasFilesForSingle || hasFilesForComparison)
      ) {
        console.log(
          "All conditions met, generating codon occupancy analysis from short code..."
        );
        setAutoRunPlot(false);
        setTimeout(() => {
          handleGenerate();
        }, 50);
      } else {
        console.log(
          "Conditions not met yet, will retry when conditions change"
        );
      }
    }
  }, [
    autoRunPlot,
    plotLoading,
    loading,
    selectedFiles.length,
    groups.map((g) => g.files.length).join(","),
    analysisMode,
    setAutoRunPlot,
  ]);

  // Smart read length adjustment (supports single + comparison modes)
  useEffect(() => {
    // Normalize file id regardless of object shape or primitive
    const normalizeFileId = (f) => {
      if (!f) return null;
      if (typeof f === "object") return f.file_id ?? f.fileId ?? f.id ?? null;
      return f;
    };

    // Gather file IDs depending on analysisMode
    let fileIdsForAdjustment = [];

    if (analysisMode === "single") {
      // groupFiles is expected to be an array of ids or file objects
      fileIdsForAdjustment = selectedFiles;
    } else {
      // comparison: combine files from all groups (support group.files or group.fileIds)
      fileIdsForAdjustment = Array.from(
        new Set(
          (groups || []).flatMap((g) => {
            if (!g) return [];
            if (Array.isArray(g.files) && g.files.length > 0) {
              return g.files.map(normalizeFileId).filter(Boolean);
            }
            if (Array.isArray(g.fileIds) && g.fileIds.length > 0) {
              return g.fileIds.map(normalizeFileId).filter(Boolean);
            }
            return [];
          })
        )
      );
    }

    // Nothing to do if no project files or no selected files
    if (!apiData?.json_files?.length || fileIdsForAdjustment.length === 0)
      return;

    // Keys used in this page's settings
    const minKey = "minRead";
    const maxKey = "maxRead";
    const currentMin = settings?.[minKey];
    const currentMax = settings?.[maxKey];

    // Only auto-adjust if both min and max are allowed
    if (
      shouldAutoAdjust(minKey, currentMin, currentMax) &&
      shouldAutoAdjust(maxKey, currentMin, currentMax)
    ) {
      const { min, max } = getStandardReadLengths(
        fileIdsForAdjustment,
        apiData.json_files
      );

      if (currentMin !== min || currentMax !== max) {
        updateMultipleSettings({ [minKey]: min, [maxKey]: max });
        toast.info(`Read lengths auto-adjusted: Min=${min}, Max=${max}`, {
          autoClose: 3000,
        });
      }
    }
  }, [
    selectedFiles,
    groups,
    analysisMode,
    apiData.json_files,
    getStandardReadLengths,
    shouldAutoAdjust,
    updateMultipleSettings,
    settings.minread,
    settings.maxread,
  ]);

  // Reset selections when analysis mode changes
  useEffect(() => {
    if (analysisMode === "single") {
      clearAllGroups();
    } else {
      clearSelection();
    }
  }, [analysisMode, clearSelection, clearAllGroups]);

  // Computed values
  const filteredFiles = useMemo(() => {
    if (!Array.isArray(apiData.json_files)) return [];
    let files = apiData.json_files;
    if (selectedSeqType && selectedSeqType !== "all") {
      files = files.filter((x) => x.file_type === selectedSeqType);
    }
    if (selectedGroup && selectedGroup !== "all") {
      files = files.filter((x) => x.subgroup === selectedGroup);
    }
    return files;
  }, [apiData.json_files, selectedSeqType, selectedGroup]);

  const seqTypes = useMemo(() => {
    if (!Array.isArray(apiData.json_files)) return [];
    const types = [...new Set(apiData.json_files.map((x) => x.file_type))];
    return types.map((type) => ({
      value: type,
      label: type.charAt(0).toUpperCase() + type.slice(1),
      count: apiData.json_files.filter((x) => x.file_type === type).length,
    }));
  }, [apiData.json_files]);

  const availableGroups = useMemo(() => {
    if (!Array.isArray(apiData.json_files)) return [];
    const groupSet = new Set(
      apiData.json_files.map((x) => x.subgroup).filter(Boolean)
    );
    return Array.from(groupSet).map((group) => ({
      value: group,
      label: group,
      count: apiData.json_files.filter((x) => x.subgroup === group).length,
    }));
  }, [apiData.json_files]);

  const allSeqTypeFilesSelected = useMemo(() => {
    if (!selectedSeqType || selectedSeqType === "all") return false;
    const seqTypeFiles = filteredFiles.filter(
      (x) => x.file_type === selectedSeqType
    );
    return (
      seqTypeFiles.length > 0 &&
      seqTypeFiles.every((x) => selectedFiles.includes(x.file_id))
    );
  }, [selectedSeqType, filteredFiles, selectedFiles]);

  const allGroupFilesSelected = useMemo(() => {
    if (!selectedGroup || selectedGroup === "all") return false;
    const groupFiles = filteredFiles.filter(
      (x) => x.subgroup === selectedGroup
    );
    return (
      groupFiles.length > 0 &&
      groupFiles.every((x) => selectedFiles.includes(x.file_id))
    );
  }, [selectedGroup, filteredFiles, selectedFiles]);

  // Event handlers
  const handleSettingUpdate = useCallback(
    (key, value) => {
      updateSetting(key, value);
      // Mark read length fields as user modified when manually changed
      if (key === "minRead" || key === "maxRead") {
        markFieldAsUserModified(key);
      }
    },
    [updateSetting, markFieldAsUserModified]
  );

  const handleReset = useCallback(() => {
    resetSettings();
    resetUserModifications();
    clearSelection();
    setAnalysisMode("single");
    resetGroups([
      { id: "group1", name: "Group 1", files: [] },
      { id: "group2", name: "Group 2", files: [] },
    ]);
    setPlotData(null);
    setError(null);
    toast.info("Settings reset to defaults");
  }, [
    resetSettings,
    resetUserModifications,
    clearSelection,
    resetGroups,
  ]);

  const handleSelectAllSeqTypeFiles = useCallback(
    (checked) => {
      if (!selectedSeqType || selectedSeqType === "all") return;
      const seqTypeFiles = filteredFiles.filter(
        (x) => x.file_type === selectedSeqType
      );
      const fileIds = seqTypeFiles.map((x) => x.file_id);

      if (checked) {
        selectMultipleFiles(fileIds);
      } else {
        deselectMultipleFiles(fileIds);
      }
    },
    [selectedSeqType, filteredFiles, selectMultipleFiles, deselectMultipleFiles]
  );

  const handleSelectAllGroupFiles = useCallback(
    (checked) => {
      if (!selectedGroup || selectedGroup === "all") return;
      const groupFiles = filteredFiles.filter(
        (x) => x.subgroup === selectedGroup
      );
      const fileIds = groupFiles.map((x) => x.file_id);

      if (checked) {
        selectMultipleFiles(fileIds);
      } else {
        deselectMultipleFiles(fileIds);
      }
    },
    [selectedGroup, filteredFiles, selectMultipleFiles, deselectMultipleFiles]
  );

  const buildPayload = useCallback(() => {
    const buildMaster = () => {
      if (analysisMode === "single") {
        return {
          group1: {
            file_ids: selectedFiles.map(String),
            file_names: selectedFiles
              .map((fileId) => {
                const file = apiData.json_files.find(
                  (f) => f.file_id === fileId
                );
                return file ? file.file_name : "";
              })
              .filter(Boolean),
          },
        };
      } else {
        const masterDict = {};
        groups.forEach((group) => {
          // Use group.id as the key instead of group.name
          masterDict[group.id] = {
            file_ids: (group.files || []).map(String),
            file_names: (group.files || [])
              .map((fileId) => {
                const file = apiData.json_files.find(
                  (f) => f.file_id === fileId
                );
                return file ? file.file_name : "";
              })
              .filter(Boolean),
          };
        });
        return masterDict;
      }
    };
    const shortCodeForThisRequest = consumeUserPassedShortCode();
    // Use the Codon Occupancy specific payload creator
    return createPlotSpecificPayload.codonOccupancy({
      settings,
      selectedFiles,
      projectId,
      userPassedShortCode: shortCodeForThisRequest,
      analysisMode,
      groups,
      buildMaster,
    });
  }, [
    settings,
    selectedFiles,
    groups,
    analysisMode,
    projectId,
    apiData.json_files,
    consumeUserPassedShortCode,
  ]);

  // Generate analysis with enhanced validation
  const handleGenerate = useCallback(async () => {
    if (plotLoading) {
      console.log("Job already in progress");
      return;
    }

    const validation = validateSettings();
    if (!validation.isValid) {
      toast.error(validation.errors[0]);
      return;
    }

    // Enhanced validation for different modes
    if (analysisMode === "single") {
      if (selectedFiles.length === 0) {
        console.log("selectedFiles.length", selectedFiles.length)
        toast.error("Please add at least one file to the analysis group");
        return;
      }
    } else {
      // Comparison mode
      const group1Files = groups[0]?.files?.length || 0;
      const group2Files = groups[1]?.files?.length || 0;

      if (group1Files === 0 || group2Files === 0) {
        toast.error("Please put files in both groups for comparison analysis");
        return;
      }

      if (!groups[0]?.name?.trim() || !groups[1]?.name?.trim()) {
        toast.error("Please provide names for both groups");
        return;
      }
    }

    const payload = buildPayload();
    console.log("Submitting codon occupancy payload:", payload);

    const onSuccess = (result) => {
      setPlotData(result.plot || result);
      setError(null);
    };

    const onError = (errorMsg) => {
      setError(errorMsg);
      setPlotData(null);
    };

    startJob(submitCodonOccupancyJob, payload, onSuccess, onError);
  }, [
    plotLoading,
    validateSettings,
    analysisMode,
    groups,
    selectedFiles.length,
    buildPayload,
    startJob,
  ]);

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="loading-overlay">
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "75vh",
              }}
            >
              <FadeLoader color="#2F9E37" size={50} />
            </div>
          </div>
        </Container>
      </div>
    );
  }

  document.title = "Codon Occupancy Analysis | EIRNA Bio Connect";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            breadcrumbs={[
              { label: "Home", url: "/dashboard" },
              {
                label: "Select Function",
                url: `/projects/${projectId}/select-function`,
              },
              { label: "Codon Occupancy " },
            ]}
          />

          <Row className="mb-4">
            <Col lg={12}>
              <div className="d-flex justify-content-between align-items-center">
                <h4
                  style={{
                    fontSize: "24px",
                    fontWeight: "600",
                    color: "#1f2937",
                    marginBottom: "0",
                  }}
                >
                  Codon Occupancy
                </h4>
                <div>
                  <Button
                    color="outline-secondary"
                    size="sm"
                    onClick={handleReset}
                    disabled={plotLoading}
                  >
                    <i className="ri-refresh-line me-1"></i>
                    Reset All
                  </Button>
                </div>
              </div>
            </Col>
          </Row>

          {/* Settings Row */}
          <Row className="mb-4">
            {/* Left: General Settings */}
            <Col lg={12}>
              <GeneralSettings
                settings={settings}
                onUpdateSetting={handleSettingUpdate}
                onReset={handleReset}
                config={codonOccupancyConfig}
                title="General Settings"
                loading={plotLoading}
                selectedFiles={selectedFiles}
                analysisMode={analysisMode}
                onSetAnalysisMode={setAnalysisMode}
              />
            </Col>
          </Row>
          <Row className="mb-4">
            {/* Right: Seq Types and Groups */}
            <Col lg={12}>
              <SeqTypesAndGroups
                seqTypes={seqTypes}
                groups={availableGroups}
                selectedSeqType={selectedSeqType}
                selectedGroup={selectedGroup}
                onSelectSeqType={setSelectedSeqType}
                onSelectGroup={setSelectedGroup}
                onSelectAllSeqTypeFiles={handleSelectAllSeqTypeFiles}
                onSelectAllGroupFiles={handleSelectAllGroupFiles}
                selectedFiles={selectedFiles}
                filteredFiles={filteredFiles}
                allSeqTypeFilesSelected={allSeqTypeFilesSelected}
                allGroupFilesSelected={allGroupFilesSelected}
                showSelectAllCheckboxes={analysisMode === "single"}
                customTitle="Seq Types & Groups"
                customIcon="ri-tools-fill"
              />
            </Col>
          </Row>

          {/* Files Card - Only show for single mode*/}
          {analysisMode === "single" && (
            <>
              <Row className="mb-3">
                <Col lg={12}>
                  <Files
                    files={filteredFiles}
                    selectedFiles={selectedFiles}
                    onToggleFile={toggleFile}
                    onSelectAllGroupFiles={handleSelectAllGroupFiles}
                    title="Select Files"
                    searchable={true}
                    selectable={true}
                    filesPerPage={30}
                    columns={6}
                    showFileCount={true}
                    customSearchFields={["file_name", "file_description"]}
                  />
                </Col>
              </Row>
              <Row>
                <Col lg={12}>
                  <SelectedFilesDisplay
                    selectedFiles={selectedFiles}
                    allFiles={apiData.json_files}
                    title="Selected Files for Codon Occupancy"
                    showSearch={true}
                    maxDisplayFiles={0}
                  />
                </Col>
              </Row>
            </>
          )}

          {/* Groups Manager - Only show for comparison mode */}
          {analysisMode === "comparison" && (
            <Row className="mb-4">
              <Col lg={12}>
                <GroupManager
                  groups={groups}
                  files={filteredFiles}
                  allFiles={apiData.json_files}
                  filteredFiles={filteredFiles}
                  onAddFilesToGroup={addFilesToGroup}
                  onRemoveFilesFromGroup={removeFilesFromGroup}
                  onUpdateGroupName={updateGroupName}
                  onClearGroup={clearGroup}
                  onAutoFill={handleAutoFill}
                  onAutoLabel={handleAutoLabel}
                  onClearAll={clearAllGroups}
                  groupLabels={{
                    group1: groups[0]?.name || "Group 1",
                    group2: groups[1]?.name || "Group 2",
                  }}
                  onUpdateGroupLabel={(groupId, name) =>
                    updateGroupName(groupId, name)
                  }
                />
              </Col>
            </Row>
          )}

          {/* Status and Generate Button */}
          <Row className="mb-4">
            <Col lg={12}>
              <div ref={loadingDivRef} 
                   className="d-flex justify-content-between align-items-center">
                <div className="text-muted">
                  {analysisMode === "single" ? (
                    selectedFiles.length === 0 ? (
                      <span>
                        <i className="ri-file-list-line me-1"></i>
                        Please select at least one file to start analysis
                      </span>
                    ) : (
                      <span>
                        <i className="ri-check-circle-line me-1 text-success"></i>
                        Ready to analyze with{" "}
                        <strong>{selectedFiles.length}</strong> file
                        {selectedFiles.length !== 1 ? "s" : ""}
                      </span>
                    )
                  ) : (
                    <span>
                      <i className="ri-group-line me-1"></i>
                      {groups[0]?.name || "Group 1"}:{" "}
                      <strong>{groups[0]?.files?.length || 0}</strong> files |{" "}
                      {groups[1]?.name || "Group 2"}:{" "}
                      <strong>{groups[1]?.files?.length || 0}</strong> files{" "}
                      {(groups[0]?.files?.length || 0) === 0 ||
                      (groups[1]?.files?.length || 0) === 0 ? (
                        <span className="text-warning ms-2">
                          Both groups need files for comparison
                        </span>
                      ) : (
                        <i className="ri-check-circle-line ms-2 text-success"></i>
                      )}
                    </span>
                  )}
                  {taskId && (
                    <span className="ms-2">
                      - <strong>Task ID:</strong> {taskId}
                    </span>
                  )}
                </div>
                <ActionButton
                  onClick={handleGenerate}
                  loading={plotLoading}
                  buttonType="plot"
                />{" "}
              </div>
            </Col>
          </Row>

          {/* Results Container */}
          {(plotData || plotLoading || error) && (
            <Row>
              <Col lg={12}>
                <PlotContainer
                  ref={plotContainerRef}
                  plotData={plotData}
                  loading={plotLoading}
                  error={error}
                  selectedFilesCount={
                    analysisMode === "single"
                      ? selectedFiles.length
                      : (groups[0]?.files?.length || 0) +
                        (groups[1]?.files?.length || 0)
                  }
                  title="Codon Occupancy Analysis Results"
                  onRetry={handleGenerate}
                  showFileCount={true}
                  isTable={false}
                />
              </Col>
            </Row>
          )}

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

export default CodonOccupancy;
